import { LinkText } from '@/components/ui/link-text';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CustomerDTO } from '@/server/dto/customer-dto';
import type { CustomerSearchRow } from '@/server/types/search-types';
import { SearchEntity } from '@/server/types/search-types';
import React from 'react';
import type { EntitySearchComboboxProps } from './entity-search-combobox';
import { EntitySearchCombobox } from './entity-search-combobox';

export type CustomerSelectProps = {
    customer?: CustomerDTO;
    value: string | undefined;
    onChange: (customerId: string, customer: CustomerSearchRow) => void;
    includeLink?: boolean;
} & Pick<EntitySearchComboboxProps, 'className' | 'variant' | 'error' | 'disabled'>;

export const CustomerSelect = React.forwardRef<HTMLDivElement, CustomerSelectProps>(
    ({ value, onChange, customer, includeLink, ...props }, ref) => {
        const { t } = useLocale();

        return (
            <div ref={ref} className="flex flex-col gap-2 w-full items-stretch">
                <EntitySearchCombobox
                    entities={[SearchEntity.Customer]}
                    inputPlaceholder={t('placeholder__select_customer', {
                        ns: 'common',
                    })}
                    defaultValue={
                        customer
                            ? [
                                  {
                                      id: customer.customerId,
                                      name: customer.customerName,
                                      entity: SearchEntity.Customer,
                                  },
                              ]
                            : undefined
                    }
                    onValueChange={(value) => {
                        const [item] = value;
                        const { original } = item || {};

                        if (original?.entity === SearchEntity.Customer && original) {
                            onChange(original.id, original);
                        }
                    }}
                    {...props}
                />
                {includeLink && value && (
                    <div className="px-2">
                        <LinkText
                            text={t('view_customer', { ns: 'common' })}
                            href={`/customers/${value}`}
                            className="text-sm text-blue-600"
                        />
                    </div>
                )}
            </div>
        );
    },
);

CustomerSelect.displayName = 'CustomerSelect';
